import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ResponsiveContainer, PieChart, Pie, Cell, Tooltip, Legend } from "recharts";
import { Trophy, AlertCircle, ChevronRight, BarChart3, Users, Crown } from "lucide-react";
import { getElectionResults, type NationalElectionResult, type ElectionPartyResult } from "@/data/electionResultsStore";
import { useI18n } from "@/i18n/I18nProvider";
import { allParties } from "@/data/parties";

export default function ElectionResultsBarometer() {
  const { lang } = useI18n();
  const isRtl = lang === "ar";
  const [data, setData] = useState<NationalElectionResult>(getElectionResults());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => { 
      const currentData = getElectionResults();
      if (currentData.config.autoUpdate && currentData.config.externalUrl) {
        try {
          // This simulates fetching from the external URL configured by the admin
          const res = await fetch(currentData.config.externalUrl);
          if (res.ok) {
            const extData = await res.json();
            // Merge external updates if necessary (not implementing full logic just simulation)
            console.log("Fetched external election data:", extData);
          }
        } catch (e) {
          console.error("Failed to fetch external election data", e);
        }
      }
      setData(getElectionResults()); 
      setLoading(false); 
    };
    
    load();
    window.addEventListener("electionResultsUpdated", load);
    
    // Check every X seconds base on config
    const intervalId = setInterval(load, 15000);
    
    return () => { 
      window.removeEventListener("electionResultsUpdated", load as EventListener); 
      clearInterval(intervalId); 
    };
  }, []);

  if (loading) return <div className="py-20 text-center animate-pulse">Chargement des résultats...</div>;

  const totalSeatsDecl = data.results.reduce((acc, curr) => acc + curr.seats, 0);
  const progressPct = Math.min(100, Math.round((totalSeatsDecl / data.totalSeats) * 100));
  const sorted = [...data.results].sort((a, b) => b.seats - a.seats);
  const topParties = sorted.slice(0, 5);

  return (
    <section className="py-12 bg-slate-950 text-slate-50 overflow-hidden relative" dir={isRtl ? "rtl" : "ltr"}>
      {/* Background aesthetics */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-slate-900 via-slate-950 to-black opacity-80" />
      <div className="absolute inset-0 bg-[url('/noise.png')] opacity-[0.03] mix-blend-overlay" />

      <div className="container relative z-10 px-4 md:px-6 mx-auto">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-red-500/10 text-red-500 font-bold text-sm mb-4 border border-red-500/20 shadow-[0_0_15px_rgba(239,68,68,0.2)]">
            <div className="w-2 h-2 rounded-full bg-red-500 animate-ping" />
            {lang === "ar" ? "مباشر : نتائج الانتخابات" : "EN DIRECT : RÉSULTATS DES ÉLECTIONS"}
          </div>
          <h2 className="text-3xl md:text-5xl font-black tracking-tight text-white mb-4 drop-shadow-md font-display">
            {lang === "ar" ? data.config.titleAr : data.config.titleFr}
          </h2>
          <p className="text-slate-400 max-w-2xl mx-auto font-medium">
            {lang === "ar"
              ? data.config.subtitleAr
              : data.config.subtitleFr}
          </p>
        </motion.div>

        {/* Progress Bar */}
        <div className="max-w-3xl mx-auto mb-12">
          <div className="flex justify-between text-sm font-bold text-slate-300 mb-2">
            <span>{lang === "ar" ? "الفرز" : "Dépouillement"}</span>
            <span className="text-amber-400 font-black" dir="ltr">
              {lang === "ar" ? `%${progressPct} (${totalSeatsDecl} / ${data.totalSeats} مقعد)` : `${progressPct}% (${totalSeatsDecl} / ${data.totalSeats} sièges)`}
            </span>
          </div>
          <div className="h-4 bg-slate-800 rounded-full overflow-hidden border border-slate-700/50 p-0.5">
            <motion.div 
              initial={{ width: 0 }} 
              animate={{ width: `${progressPct}%` }} 
              transition={{ duration: 1.5, ease: "easeOut" }}
              className="h-full bg-gradient-to-r from-amber-500 to-amber-300 rounded-full relative"
            >
              <div className="absolute inset-0 bg-white/20 animate-pulse" />
            </motion.div>
          </div>
        </div>

        {/* Main Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Top Rankings List */}
          <div className="lg:col-span-5 space-y-3">
            <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <Trophy className="w-5 h-5 text-amber-400" />
              {lang === "ar" ? "مقدمة السباق" : "Tête de course"}
            </h3>
            {topParties.map((party, index) => (
              <motion.div 
                key={party.partyAcronym}
                initial={{ opacity: 0, x: -20 }} 
                animate={{ opacity: 1, x: 0 }} 
                transition={{ delay: index * 0.1 }}
                className="bg-slate-900/80 backdrop-blur border border-slate-800 p-4 rounded-2xl flex items-center gap-4 hover:bg-slate-800/80 transition-colors"
              >
                <div className={`w-10 h-10 rounded-full flex items-center justify-center font-black text-lg shrink-0 ${index === 0 ? "bg-amber-500/20 text-amber-500 ring-2 ring-amber-500/50 shadow-[0_0_10px_rgba(245,158,11,0.3)]" : "bg-slate-800 text-slate-300"}`}>
                  {index === 0 ? <Crown className="w-5 h-5" /> : index + 1}
                </div>
                <div className="flex-1">
                  <div className="flex justify-between items-end mb-1">
                    <h4 className="font-bold text-lg text-white leading-none">{party.partyAcronym}</h4>
                    <span className="font-black text-2xl text-amber-400 leading-none">{party.seats}<span className="text-sm font-normal text-slate-400 ml-1">{lang === "ar" ? "مقاعد" : "sièges"}</span></span>
                  </div>
                  <div className="w-full h-1.5 bg-slate-800 rounded-full mt-2">
                    <motion.div 
                      initial={{ width: 0 }} 
                      animate={{ width: `${(party.seats / (topParties[0]?.seats || 1)) * 100}%` }}
                      className="h-full rounded-full"
                      style={{ backgroundColor: allParties.find(p => p.acronym === party.partyAcronym)?.color || '#3b82f6' }}
                    />
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Half Donut Hemicycle Representation */}
          <div className="lg:col-span-7 bg-slate-900/50 backdrop-blur border border-slate-800/80 rounded-3xl p-6 flex flex-col items-center justify-center min-h-[400px]">
            <h3 className="text-xl font-bold text-white mb-6 self-start flex items-center gap-2 w-full">
              <Users className="w-5 h-5 text-blue-400" /> 
              {lang === "ar" ? "التشكيلة المرتقبة" : "Projection de l'Hémicycle"}
            </h3>
            <div className="w-full aspect-[2/1] relative max-w-md mx-auto mt-4">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={sorted}
                    cx="50%"
                    cy="100%"
                    startAngle={180}
                    endAngle={0}
                    innerRadius="60%"
                    outerRadius="100%"
                    paddingAngle={2}
                    dataKey="seats"
                    stroke="none"
                  >
                    {sorted.map((entry, index) => {
                      const partyDef = allParties.find(p => p.acronym === entry.partyAcronym);
                      const fill = partyDef ? partyDef.color : ['#3b82f6', '#f59e0b', '#10b981', '#ef4444', '#8b5cf6', '#06b6d4', '#f97316'][index % 7];
                      return <Cell key={`cell-${index}`} fill={fill} />;
                    })}
                  </Pie>
                  <Tooltip 
                     contentStyle={{ backgroundColor: '#0f172a', border: '1px solid #1e293b', borderRadius: '12px', color: '#f8fafc' }}
                     itemStyle={{ color: '#f8fafc', fontWeight: 'bold' }}
                     formatter={(value: number, name: string) => [`${value} ${lang === "ar" ? "مقاعد" : "sièges"}`, name]}
                  />
                </PieChart>
              </ResponsiveContainer>
              <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1/4 text-center">
                <div className="text-4xl font-black text-white">{totalSeatsDecl}</div>
                <div className="text-xs font-bold text-slate-400 uppercase tracking-widest mt-1">{lang === "ar" ? "مقاعد مخصصة" : "Sièges attribués"}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
